import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { downloadTemplate } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Overline, Loading, UpgradeBanner } from "@/components/shared";

export default function TemplateLibrary() {
  const { entitlements } = useAuth();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/content/templates").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const visible = data.templates.filter((t) => t.unlocked);
  const availCats = [...new Set(visible.map((t) => t.category))];
  const filtered = cat === "All" ? visible : visible.filter((t) => t.category === cat);

  const TOOLKITS = [
    { part: "part_1", label: "Toolkit 1 · The Role", t: { icon: "bg-nm-green/12 text-nm-green", cat: "text-nm-coral", btn: "bg-nm-coral", border: "border-nm-primary/12", bg: "bg-nm-surface", bar: "bg-nm-primary", head: "text-nm-primary", chip: "text-nm-primary bg-nm-primary/[0.07]" } },
    { part: "part_2", label: "Toolkit 2 · The People", t: { icon: "bg-nm-plum/14 text-nm-plum", cat: "text-nm-plum", btn: "bg-nm-plum", border: "border-nm-plum/20", bg: "bg-nm-mauveBg/60", bar: "bg-nm-plum", head: "text-nm-plum", chip: "text-nm-plum bg-nm-plum/[0.10]" } },
    { part: "part_3", label: "Toolkit 3 · The Moments", t: { icon: "bg-nm-highlight/40 text-amber-700", cat: "text-amber-700", btn: "bg-amber-500", border: "border-amber-300/50", bg: "bg-amber-50/60", bar: "bg-amber-400", head: "text-amber-700", chip: "text-amber-700 bg-amber-100" } },
  ];
  const groups = TOOLKITS
    .map((tk) => ({ ...tk, items: filtered.filter((t) => t.part === tk.part) }))
    .filter((g) => g.items.length > 0);

  const handleDownload = async (t) => {
    setBusy(t.slug);
    await downloadTemplate(t.slug, t.title);
    await load();
    setBusy(null);
  };

  const Card = ({ t, i, tk }) => (
    <motion.div key={t.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
      className={`flex flex-col rounded-2xl border ${tk.border} ${tk.bg} p-6 nm-shadow-sm`} data-testid={`template-card-${t.slug}`}>
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-xl ${tk.icon} flex items-center justify-center`}><FileText size={18} /></div>
        {t.unlocked
          ? (t.downloaded && <span className="inline-flex items-center gap-1 text-xs text-nm-green"><CheckCircle2 size={13} /> Downloaded</span>)
          : <Lock size={15} className="text-nm-neutral/50" />}
      </div>
      <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${tk.cat} mt-4`}>{t.category}</div>
      <div className="font-serif text-xl text-nm-ink mt-1.5" data-testid={`template-title-${t.slug}`}>{t.title}</div>
      <p className="text-sm text-nm-neutral mt-2 leading-relaxed flex-1">{t.summary}</p>
      <div className="flex items-center gap-2 mt-5">
        <button onClick={() => handleDownload(t)} disabled={busy === t.slug} data-testid={`download-btn-${t.slug}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full ${tk.btn} px-4 py-2.5 text-sm font-medium text-white hover:brightness-105 disabled:opacity-60 transition-[filter] duration-200`}>
          <Download size={15} /> {busy === t.slug ? "Preparing…" : "Download PDF"}
        </button>
      </div>
    </motion.div>
  );

  return (
    <div data-testid="template-library">
      <Overline>Template Library</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2">Tools you can use today</h1>
      <p className="text-lg text-nm-neutral font-light mt-4 max-w-2xl">Every template downloads as a clean, branded, fillable PDF you can type into and save. Locked templates unlock with the part they belong to.</p>

      {!entitlements.bundle_owner && <div className="mt-8"><UpgradeBanner entitlements={entitlements} /></div>}

      <div className="flex flex-wrap gap-2 mt-8">
        {["All", ...availCats].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c.toLowerCase()}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${cat === c ? "bg-nm-primary text-white" : "bg-white border border-nm-primary/12 text-nm-neutral hover:text-nm-ink"}`}>
            {c}
          </button>
        ))}
      </div>

      {groups.map((g) => (
        <section key={g.part} className="mt-12" data-testid={`toolkit-group-${g.part}`}>
          <div className="flex items-center gap-3">
            <span className={`h-6 w-1.5 rounded-full ${g.t.bar}`} />
            <h2 className={`font-serif text-2xl ${g.t.head}`}>{g.label}</h2>
            <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${g.t.chip}`}>{g.items.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
            {g.items.map((t, i) => <Card key={t.slug} t={t} i={i} tk={g.t} />)}
          </div>
        </section>
      ))}
      {groups.length === 0 && (
        <p className="mt-10 text-nm-neutral" data-testid="no-templates">
          {visible.length === 0
            ? "Your templates will appear here once you unlock a toolkit."
            : "No templates in this category yet."}
        </p>
      )}
    </div>
  );
}
